/**
 * The API-key records as `/v1/admin/api-keys` puts them on the wire, and the
 * few derivations the panel needs.
 *
 * The types are declared here rather than imported from the admin portal's
 * own types directory: that portal has been retired, and a screen that
 * outlives it must not depend on it. The spelling is the gateway's own
 * (snake_case) — these are
 * wire records, and renaming them here would only hide where they come from.
 */

export interface ApiKey {
  id: string
  name: string
  created_at: string
  /** The e-mail of whoever created the key. An administrator's listing is not
   *  scoped to them, so this is the only way to tell a foreign key apart. */
  created_by?: string
  user_id?: string
  last_used_at?: string
  is_active: boolean
}

/** Only ever present in the answer to `POST /v1/admin/api-keys`. */
export interface ApiKeyWithToken extends ApiKey {
  token: string
}

export interface CreateApiKeyRequest {
  name: string
  description?: string
}

/**
 * The listing answers `{api_keys, total}`. It is read defensively because a
 * failed or half-configured answer must render as "keine Schlüssel", never as
 * a crash inside the settings page.
 */
export function apiKeysFrom(payload: unknown): ApiKey[] {
  if (Array.isArray(payload)) return payload as ApiKey[]
  const list = (payload as { api_keys?: unknown } | null | undefined)?.api_keys
  return Array.isArray(list) ? (list as ApiKey[]) : []
}

export function activeKeyCount(keys: ApiKey[]): number {
  return keys.filter((key) => key.is_active).length
}

/**
 * What the "Schlüssel" column can honestly show.
 *
 * The gateway stores only the key's hash and returns no fragment of the token,
 * so this is the key's **id** — an identifier, not part of the secret. It is
 * shortened because the full uuid is longer than the column and says nothing
 * more; the table's footnote says what it is.
 */
export function shortKeyId(id: string, keep = 12): string {
  return id.length > keep ? `${id.slice(0, keep)}…` : id
}

const DATE = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
const DATE_TIME = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

function parse(value: string | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** A date we cannot read is shown as unknown — never as today. */
export function formatKeyDate(value: string | undefined): string {
  const date = parse(value)
  return date ? DATE.format(date) : 'unbekannt'
}

/**
 * `last_used_at` is omitted by the gateway for a key that has never been used,
 * which is a fact worth stating rather than an empty cell.
 */
export function formatLastUsed(value: string | undefined): string {
  if (!value) return 'nie benutzt'
  const date = parse(value)
  return date ? DATE_TIME.format(date) : 'unbekannt'
}

/** Newest first, so a key just created is the first row of the table. */
export function sortKeys(keys: ApiKey[]): ApiKey[] {
  return [...keys].sort((a, b) => {
    const left = parse(a.created_at)?.getTime() ?? 0
    const right = parse(b.created_at)?.getTime() ?? 0
    return right - left
  })
}

/**
 * A German sentence for a failed key call.
 *
 * The gateway answers these endpoints in English ("Failed to create API key",
 * "Access denied"), so passing `error.message` through would put English in
 * front of a German-speaking account. Only the cases that mean something
 * different to the reader are named; everything else gets the caller's own
 * sentence, which says which action failed.
 */
export function apiKeyErrorMessage(error: unknown, fallback: string): string {
  const status = (error as { status?: number } | null)?.status
  const code = (error as { code?: string } | null)?.code
  if (code === 'network_error') return 'Keine Verbindung zum Server.'
  switch (status) {
    case 401:
      return 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'
    case 403:
      return 'Sie dürfen diesen Schlüssel nicht verwalten.'
    case 404:
      return 'Dieser Schlüssel besteht nicht mehr.'
    case 503:
      return 'Die Schlüsselverwaltung ist in dieser Installation nicht eingerichtet.'
    default:
      return fallback
  }
}
